import { createContext, useState, useRef, useEffect, ReactNode } from 'react';
import { BlogContent, GenerateBlogRequest } from './types';
import blogService from './services/blogApi';
import { toast } from 'react-hot-toast';
import remarkHtml from 'remark-html';
import remarkMdx from 'remark-mdx';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { extractImagesFromMarkdown } from './components/Modals/blogSaveAs';

export interface BlogContextType {
  blogContent: BlogContent | null;
  isGenerating: boolean;
  isSettingBlog: boolean;
  handleGenerateBlog: (formData: GenerateBlogRequest) => Promise<void>;
  handleUpdateBlogContent: (data: BlogContent) => void;
  handleUpdateHtmlDump: (data: string) => void;
  htmlDump: string | null;
  cleanUp: () => void;
}

// Create context with initial values
export const BlogContext = createContext<BlogContextType>({
  blogContent: null,
  isGenerating: false,
  isSettingBlog: false,
  handleGenerateBlog: async () => {},
  handleUpdateBlogContent: () => {},
  handleUpdateHtmlDump: () => {},
  htmlDump: null,
  cleanUp: () => {},
});

export const DEFAULT_BLOG_CONTENT: BlogContent = {
  title: '',
  content: '',
  images: [],
  wordCount: 0,
  markDownContent: '',
  prevState: '',
  settings: {
    minLength: '0',
    maxLength: '0',
    language: '',
    toneOfVoice: '',
    industry: '',
    imageCount: 0,
    permission: 2,
  },
};

interface BlogProviderProps {
  children: ReactNode;
}

export const BlogProvider = ({ children }: BlogProviderProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSettingBlog, setIsSettingBlog] = useState(false);
  const [blogContent, setBlogContent] = useState<BlogContent | null>(
    DEFAULT_BLOG_CONTENT
  );
  const [htmlDump, setHtmlDump] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [requestCancel, setRequestCancel] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const cleanUp = () => {
    if (abortControllerRef.current) {
      setRequestCancel(true);
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsGenerating(false);
      // Only show toast if this wasn't triggered by component unmount
      if (document.body) {
        toast.success('Blog generation cancelled successfully');
      }
    }
    setBlogContent(DEFAULT_BLOG_CONTENT);
    setHtmlDump(null);
  };

  const handleUpdateBlogContent = (data: BlogContent) => {
    setBlogContent(data);
  };

  const handleUpdateHtmlDump = (data: string) => {
    setHtmlDump(data);
  };

  const handleGenerateBlog = async (formData: GenerateBlogRequest) => {
    // Reset cancel state at the start of new generation
    setRequestCancel(false);

    // If there's an ongoing request, cancel it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    setIsGenerating(true);

    try {
      const response = await blogService.generateBlog(formData);

      // Only process the response if the request wasn't aborted and we haven't cancelled
      if (!requestCancel && !abortControllerRef.current?.signal.aborted) {
        if (response.success && response.data) {
          const markdownContent = response.data.blog.context || '';

          const htmlString = await unified()
            .use(remarkParse)
            .use(remarkMdx)
            .use(remarkHtml)
            .process(markdownContent);

          handleUpdateHtmlDump(htmlString.toString());

          const imageUrls = response.data.images_urls || '';
          const blogData: BlogContent = {
            id: response.data.id,
            title: response.data.title || formData.topic,
            content: htmlString.toString(),
            images: imageUrls ? imageUrls.split(',').filter(Boolean) : [],
            prevState: htmlString.toString(),
            wordCount: 0, // This will be calculated by the editor component
            markDownContent: markdownContent,
            settings: {
              minLength: String(formData.min_len),
              maxLength: String(formData.max_len),
              language: formData.language,
              toneOfVoice: formData.tone,
              industry: formData.industry,
              imageCount: formData.img_number,
              permission: 2,
            },
          };

          handleUpdateBlogContent(blogData);
          toast.success('Blog generated successfully');
        } else {
          toast.error(response.message || 'Failed to generate blog');
        }
      }
    } catch (error: any) {
      // Check for both abort error and manual cancellation
      if (error?.name === 'AbortError' || requestCancel) {
        console.log('Request was cancelled');
        return;
      }

      console.error('Error generating blog:', error);
      toast.error('Failed to generate blog content');
    } finally {
      // Reset states if this wasn't a cancellation
      if (!requestCancel && !abortControllerRef.current?.signal.aborted) {
        setIsGenerating(false);
        abortControllerRef.current = null;
      }
      // Reset requestCancel state
      setRequestCancel(false);
    }
  };

  const value: BlogContextType = {
    blogContent,
    isGenerating,
    isSettingBlog,
    handleGenerateBlog,
    handleUpdateBlogContent,
    handleUpdateHtmlDump,
    htmlDump,
    cleanUp,
  };

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
};
